<template>

  <v-container fluid class="page page-shipper-org-item">

    <!-- Breadcrumb & Actions -->
    <v-row class="mt-4">
      <v-col cols="8" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-account-circle-outline</v-icon>
        <router-link :to="{ name: 'shipper-org-list' }">Shipper Orgs</router-link>
        <div class="separator">/</div>
        <div class="active-item" v-if="org">{{ org.name }}</div>
      </v-col>
      <v-col cols="4" align="right">
        <!-- (edit org action) -->
        <v-btn color="orange darken-3" @click="editItem">
          Edit Shipper Org
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="org">
      <!--------------------->
      <!-- Org Information -->
      <!--------------------->
      <v-row class="mt-8 mb-4">
        <v-col cols="12">
          <div class="page-title">{{ org.name }}</div>
        </v-col>
      </v-row>

      <v-container fluid class="mt-2 page-content">
        <!-- Org name -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="4" md="3" lg="2">
            <span class="attr-label">Org name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="9" lg="10">
            <span>{{ org.name }}</span>
          </v-col>
        </v-row>

        <!-- Owner email -->
        <v-row class="pa-0 ma-0 mt-3">
          <v-col cols="12" xs="12" sm="4" md="3" lg="2">
            <span class="attr-label">Owner email:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="9" lg="10">
            <span>{{ ownerEmail }}</span>
          </v-col>
        </v-row>

        <!-- Joined date -->
        <v-row class="pa-0 ma-0 mt-3">
          <v-col cols="12" xs="12" sm="4" md="3" lg="2">
            <span class="attr-label">Joined {{ appName }}:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="9" lg="10">
            <span>{{ formatDateTime(org.created_at) }}</span>
          </v-col>
        </v-row>

        <!-- Status -->
        <v-row class="pa-0 ma-0 mt-3" align="center">
          <v-col cols="12" xs="12" sm="4" md="3" lg="2">
            <span class="attr-label">Status:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="9" lg="10">
            <v-chip label outlined :class="org.org_status" v-if="org.org_status">
              {{ org.org_status.toUpperCase() }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Claim link -->
        <v-row class="pa-0 ma-0 mt-2" align="center" v-if="org.org_status === 'registered' && claimLink">
          <v-col cols="12" xs="12" sm="12" md="3" lg="2">
            <span class="attr-label">Claim link:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="11" md="6" lg="6">
            <v-text-field
                style="cursor:pointer"
                color="primary"
                outlined
                readonly
                hide-details
                :value="claimLink"
                append-outer-icon="mdi-content-copy">
              <template v-slot:append-outer>
                <v-btn icon color="primary" @click="copyClaimLink">
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" xs="12" sm="4" md="3" lg="2" align="center">
            <div v-if="isEmailSending">
              <v-progress-circular indeterminate :size="20" color="primary" />
            </div>
            <div class="success-message-text" v-else-if="emailSentMessage">
              {{ emailSentMessage }}
            </div>
            <dialog-confirm-button
                v-else
                buttonColor="primary"
                :message="generateSendEmailConfirmationMessage"
                buttonText="Send Email"
                v-on:confirmed="sendClaimAccountEmail" />
          </v-col>
        </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'ShipperOrgItemPage',

  components: {
    DialogConfirmButton
  },

  props: {
    orgId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      org: null,
      // --------- Send email handling
      isEmailSending: false,
      emailSentMessage: null,
      emailSendingError: null
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    // The org registration code data
    orgRegistration () {
      return (this.org)
        ? this.org.registration_code
        : null
    },
    // The assigned owner email
    ownerEmail () {
      return (this.orgRegistration)
        ? this.orgRegistration.email
        : null
    },
    // The org claim link
    claimLink () {
      return (this.orgRegistration)
        ? `${this.$root.appBaseUrl()}/claim-account?registration_code=${this.orgRegistration.registration_code}`
        : null
    },
    generateSendEmailConfirmationMessage () {
      return `Send claim account email to ${this.orgRegistration.email}?`
    }
  },

  async beforeMount () {
    // Load org
    await this.loadOrg()
  },

  async beforeDestroy () {
    this.emailSendingError = null
    this.emailSentMessage = null
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'YYYY/MM/DD h:mm a') : ''
    },

    // Copy claim link to clipboard
    copyClaimLink () {
      if (this.claimLink) this.$root.copyToClipboard(this.claimLink)
    },

    // Hide confirmation messages on timer
    autoHideMessage () {
      setTimeout(() => {
        this.emailSentMessage = null
      }, 4000)
    },

    // Navigate to edit page
    editItem () {
      this.$root.goTo('shipper-org-item-edit', { orgId: this.orgId })
    },

    // Load org with registration info
    async loadOrg () {
      this.isDataLoading = true

      try {
        this.org = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `shipper-org/${this.orgId}`,
          opts: {
            params: {
              with: 'registration_code'
            }
          }
        })
      } catch (err) {
        console.error('Error loading receiver org:', err)
      } finally {
        this.isDataLoading = false
      }
    },

    // Trigger email for claim account
    async sendClaimAccountEmail () {
      if (!this.orgRegistration) return false
      this.isEmailSending = true

      try {
        // Trigger email notification
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'org/send-account-claim-email',
          opts: {
            bodyData: {
              owner_email: this.orgRegistration.email,
              registration_code: this.orgRegistration.registration_code
            }
          },
          to_model: false
        })

        // Show confirmation message
        this.emailSentMessage = 'Email sent!'
        this.autoHideMessage()
      } catch (err) {
        this.emailSendingError = err.message || err.text || err
      } finally {
        this.isEmailSending = false
      }
    }
  }
}
</script>

<style>
  .page-shipper-org-item .section-title {
    font-size: 1.2rem;
    color: #E0E0E0;
  }

  .page-shipper-org-item .success-message-text {
    font-size: 1.23em;
    color: #4CAF50;
    font-weight: 400;
  }

  .page-shipper-org-item .v-text-field--outlined .v-input__append-outer {
    margin-top: 10px !important;
  }
</style>
